import React, { useState, useEffect, useRef } from 'react';
import useAuth from 'hooks/useAuth';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Button, TextField } from '@mui/material';
import Paragraph from 'components/atoms/Paragraph/Paragraph';
import PasswordInputField from 'components/atoms/PasswordInputField/PasswordInputField';
import Heading from 'components/atoms/Heading/Heading';
import Breadcrumbs from 'components/atoms/Breadcrumbs/Breadcrumbs';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import { theme } from 'theme/MainTheme';
import axiosInstance from 'api/axios';
import { useTranslation } from 'react-i18next';

const StyledParagraph = styled(Paragraph)`
  margin-top: 10px;
  text-decoration: none;
  font-size: 1.3rem;
  text-align: center;
  color: ${theme.primary};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledHeading = styled(Heading)`
  text-align: center;
  font-size: 2rem;
`;

const StyledForm = styled.form`
  .MuiFormLabel-root,
  .MuiInputLabel-shrink,
  #username-label,
  #password-label {
    font-size: 1.4rem;
  }
`;

const LoginStandard = ({ loginStandard, apiUrl }) => {
  const { setAuth } = useAuth();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const userRef = useRef();
  const errRef = useRef();
  const [loginForm, setLoginForm] = useState({ pwd: '', user: localStorage.getItem('user') });
  const [errMsg, setErrMsg] = useState('');
  const [isAuth, setIsAuth] = useState(true);

  useEffect(() => {
    userRef.current.focus();
  }, []);

  useEffect(() => {
    setErrMsg('');
  }, [loginForm.user, loginForm.pwd]);

  const validate = () => {
    if (!loginForm.user) {
      return t('please_give_geid');
    }
    if (!loginForm.pwd) {
      return t('password_empty');
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorMsg = validate(loginForm);
    if (errorMsg) {
      setErrMsg(errorMsg);
      return;
    }

    try {
      const response = await axiosInstance.post(
        apiUrl,
        loginStandard
          ? JSON.stringify({ geid: loginForm.user, password: loginForm.pwd })
          : JSON.stringify({ login: loginForm.user, password: loginForm.pwd }),
      );
      const roles = response?.data?.roles || [];
      localStorage.setItem('user', loginForm.user);
      localStorage.setItem('roles', JSON.stringify(roles));

      setAuth({
        user: localStorage.getItem('user'),
        pwd: loginForm.pwd,
        roles: localStorage.getItem('roles'),
        isAuth,
      });
      navigate(from, { replace: true });
    } catch (err) {
      if (!err?.response) {
        setErrMsg(t('no_serwer_response_inf'));
      }
      else
        switch (err.response?.status)
        {
          case 400:
            setErrMsg(t('geid_password_inf'));
            break;
          case 401:
            setErrMsg(t('no_authorization_inf'));
            setIsAuth(false);
            localStorage.setItem('user', '');
            localStorage.setItem('roles', JSON.stringify([]));
            break;
          case 423:
            setErrMsg(t('user_block_inf'));
            break;
          case 424:
            setErrMsg(t('account_inactive_24'));
            break;
          case 425:
            setErrMsg(t('account_blocked_24'));
            break;
          default:
            setErrMsg(t('login_err_inf'));
            break;    
        }

      errRef.current.focus();
    }
  };

  const goToFirstLogin = () => {
    navigate('/first_login');
  };

  const handlePasswordChange = (e) => {
    setLoginForm({ ...loginForm, pwd: e.target.value });
  };

  return (
    <section>
      <StyledHeading>{t('login')}</StyledHeading>
      {errMsg && (
        <Stack sx={{ maxWidth: '400px' }} spacing={0}>
          <Alert
            severity="error"
            sx={{ alignItems: 'center', fontSize: '1rem', height: 'fit-content' }}
          >
            {errMsg}
          </Alert>
        </Stack>
      )}
      <StyledForm onSubmit={handleSubmit}>
        <TextField
          autoFocus
          margin="normal"
          variant="standard"
          id="username"
          label="GEID"
          type="text"
          fullWidth
          inputRef={userRef}
          onChange={(e) => setLoginForm({ ...loginForm, user: e.target.value })}
          value={loginForm.user}
        />
        <PasswordInputField
          label={t('password')}
          id="password"
          handlePasswordChange={handlePasswordChange}
          value={loginForm.pwd}
        />
        <ButtonWrapper>
          <Button
            type="submit"
            variant="contained"
            sx={{ backgroundColor: theme.primary, mb: 1, mt: 1, width: '150px' }}
          >
            {t('log_in')}
          </Button>
          {loginStandard ? (
            <>
              <Button
                type="button"
                variant="contained"
                sx={{ backgroundColor: theme.primary, width: '150px' }}
                onClick={goToFirstLogin}
              >
                {t('first_login')}
              </Button>
              <StyledParagraph as={NavLink} to="/password_change">
                {t('change_password')}
              </StyledParagraph>
            </>
          ) : null}
        </ButtonWrapper>
      </StyledForm>
      <Breadcrumbs>
        <span>GEID:</span> {t('geid_definition')} {t('geid_definition_disclaimer')}
      </Breadcrumbs>
    </section>
  );
};

export default LoginStandard;
